import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import CompanySelector from '@components/shared/CompanySelector';
import { parkeKategorileri } from '@mocks/materials/parke';
import { mockCompanies } from '@mocks/firmalar';
import { calculateKDV, DEFAULT_KDV_RATE } from '@utils/kdv';
import { TEDARIK_KAYNAKLARI, stokYeterliMi } from '@utils/stok';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import KDVSummary from '../../components/KDVSummary';

export default function ParkeModal({ isOpen, onClose, onSubmit, onMaterialSubmit, submitLabel, initialData = null, isLoading = false }) {
    const isEdit = !!initialData;
    const [selectedCompany, setSelectedCompany] = useState(
        initialData?.supplierId ? { id: initialData.supplierId, name: initialData.tedarikci } : null
    );
    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: initialData || {
            siparisTarihi: new Date().toISOString().split('T')[0],
            kategori: 'laminat', birim: 'kutu', kdvOrani: DEFAULT_KDV_RATE, tedariKaynagi: 'tedarikci',
        },
    });

    const watchedBirimFiyat = watch('birimFiyat');
    const watchedMiktar = watch('miktar');
    const watchedKdvOrani = watch('kdvOrani');
    const watchedKaynak = watch('tedariKaynagi');
    const watchedKategori = watch('kategori');

    const stokHavuzuParke = getStokHavuzu('parke');
    const secilenKategoriLabel = parkeKategorileri.find(k => k.key === watchedKategori)?.label?.split(' ')[0] || '';
    const mevcutStok = stokHavuzuParke.find(s => s.altTip === secilenKategoriLabel);
    const serbestStok = mevcutStok?.serbestStok || 0;
    const miktarNum = parseFloat(watchedMiktar) || 0;
    const stokYeterli = stokYeterliMi(serbestStok, miktarNum);
    const isStoktan = watchedKaynak === 'stoktan';

    const handleFormSubmit = (data) => {
        if (data.tedariKaynagi === 'stoktan') {
            if (!stokYeterli) return;
            data.birimFiyat = 0; data.toplamFiyat = 0; data.kdvOrani = 0; data.kdvTutari = 0; data.kdvDahilToplam = 0; data.tedarikci = null;
        } else {
            const result = calculateKDV({ unitPrice: data.birimFiyat, quantity: data.miktar, vatRate: data.kdvOrani });
            data.toplamFiyat = result.toplamHaric;
            data.kdvOrani = result.kdvOrani;
            data.kdvTutari = result.kdvTutari;
            data.kdvDahilToplam = result.toplamDahil;
            data.tedarikci = selectedCompany?.name || data.tedarikci;
            data.supplierId = selectedCompany?.id || null;
        }
        data.stokMiktar = data.stokMiktar || data.miktar;
        data.kullanilanMiktar = data.miktar - data.stokMiktar;

        const enriched = {
            ...data,
            selectedCompany: isStoktan ? null : selectedCompany,
            calculatedTotal: data.kdvDahilToplam,
            autoDescription: `Parke Alımı — ${data.miktar} m² ${data.kategori}`,
        };

        if (onMaterialSubmit) {
            onMaterialSubmit(enriched);
        } else {
            onSubmit(enriched, isEdit);
        }
    };

    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50";

    return (
        <Modal isOpen={isOpen} onClose={() => { reset(); onClose(); }} title={isEdit ? 'Parke Siparişi Düzenle' : 'Yeni Parke Siparişi'} size="lg"
            footer={<><Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit(handleFormSubmit)} isLoading={isLoading} disabled={isStoktan && !stokYeterli}>{submitLabel || (isEdit ? 'Güncelle' : 'Sipariş Oluştur')}</Button></>}>
            <form className="space-y-5">
                {/* Tedarik Kaynağı */}
                <div className="p-3 bg-bg-tertiary rounded-lg">
                    <label className="block text-sm font-medium text-text-primary mb-2">Tedarik Kaynağı <span className="text-error">*</span></label>
                    <div className="flex gap-3">
                        {TEDARIK_KAYNAKLARI.map(tk => (
                            <label key={tk.key} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all ${watchedKaynak === tk.key ? 'border-primary bg-primary/5 text-primary' : 'border-border-light text-text-secondary hover:bg-bg-hover'}`}>
                                <input type="radio" {...register('tedariKaynagi')} value={tk.key} className="sr-only" />
                                <span className="text-sm font-medium">{tk.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {isStoktan && (
                    <div className={`p-3 rounded-lg border ${stokYeterli || miktarNum === 0 ? 'bg-info/5 border-info/30' : 'bg-error/5 border-error/30'}`}>
                        <p className="text-sm text-text-secondary"><strong>{secilenKategoriLabel}</strong> kategorisinde serbest stok: <span className="font-bold text-success">{serbestStok} kutu</span></p>
                        {miktarNum > 0 && !stokYeterli && (<p className="text-sm text-error mt-1 font-medium">⚠ Yetersiz stok! İstenen: {miktarNum} kutu</p>)}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CompanySelector value={selectedCompany} onChange={setSelectedCompany} companies={mockCompanies} label="Tedarikçi / Firma" disabled={isStoktan} />
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Sipariş Tarihi <span className="text-error">*</span></label><input type="date" {...register('siparisTarihi', { required: true })} className={inputClass} /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Kategori <span className="text-error">*</span></label>
                        <select {...register('kategori', { required: true })} className={inputClass}>{parkeKategorileri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}</select></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Ürün Adı <span className="text-error">*</span></label><input type="text" {...register('urunAdi', { required: true })} placeholder="Ürün adı" className={`${inputClass} ${errors.urunAdi ? 'border-error' : ''}`} /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Ebat</label><input type="text" {...register('ebat')} placeholder="1380x193mm" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Kalınlık</label><input type="text" {...register('kalinlik')} placeholder="8mm" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Durum</label><select {...register('durum')} className={inputClass}><option value="bekliyor">Bekliyor</option><option value="teslim">Teslim Edildi</option></select></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Miktar (kutu) <span className="text-error">*</span></label><input type="number" step="1" {...register('miktar', { required: true, valueAsNumber: true })} placeholder="0" className={inputClass} /></div>
                    {!isStoktan && (<>
                        <div><label className="block text-sm font-medium text-text-primary mb-1.5">Birim Fiyat (₺/kutu)</label><input type="number" {...register('birimFiyat', { valueAsNumber: true })} placeholder="320" className={inputClass} /></div>
                        <div><label className="block text-sm font-medium text-text-primary mb-1.5">KDV Oranı (%)</label><input type="number" step="1" {...register('kdvOrani', { valueAsNumber: true })} placeholder="20" className={inputClass} /></div>
                    </>)}
                </div>

                {!isStoktan && <KDVSummary unitPrice={watchedBirimFiyat} quantity={watchedMiktar} vatRate={watchedKdvOrani} />}

                <div><label className="block text-sm font-medium text-text-primary mb-1.5">Not</label><textarea {...register('not')} rows={2} placeholder="Sipariş notu..." className={`${inputClass} resize-none`} /></div>
            </form>
        </Modal>
    );
}
